import cn from 'classnames';
import { forwardRef } from 'react';

import type { ForwardedRef, ReactElement } from 'react';

import type { ListboxGroupLabelProps } from './listbox-group-label.types';

import './listbox-group-label.scss';

/**
 * The `ListboxGroupLabel` component is used to display label inside list group.
 *
 * ### Usage
 *
 * ```tsx
 * import { Listbox, ListboxItem, ListboxGroup, ListboxGroupLabel } from '@bloomreach/react-banana-ui';
 *
 * export default function MyGroupedList() {
 *   return (
 *      <Listbox>
 *        <ListboxGroup>
 *          <ListboxGroupLabel>Group one</ListboxGroupLabel>
 *          <ListboxItem>Item 1</ListboxItem>
 *          <ListboxItem>Item 2</ListboxItem>
 *          <ListboxItem>Item 3</ListboxItem>
 *        </ListboxGroup>
 *        <ListboxGroup>
 *          <ListboxGroupLabel>Group two</ListboxGroupLabel>
 *          <ListboxItem>Item 1</ListboxItem>
 *          <ListboxItem>Item 2</ListboxItem>
 *          <ListboxItem>Item 3</ListboxItem>
 *        </ListboxGroup>
 *      </Listbox>
 *   );
 * }
 * ```
 */
const ListboxGroupLabel = forwardRef(
  (props: ListboxGroupLabelProps, forwardedRef: ForwardedRef<HTMLLIElement> | null): ReactElement => {
    const {
      children,
      className,
      ...rest // Capture any other properties to pass them to the root element, like `aria-*`, `data-*`, etc.
    } = props;

    const classes = cn('rbui-listbox-group-label', className);

    return (
      <li className={classes} role="presentation" {...rest} ref={forwardedRef}>
        {children}
      </li>
    );
  },
);

ListboxGroupLabel.displayName = 'ListboxGroupLabel';

export default ListboxGroupLabel;
